import React, { FC } from 'react'
import { Input } from 'shared/components/Input'
import { Button } from 'shared/components/Button'
import { Autocomplete } from 'shared/components/Autocomplete'
import { useGeneralInfoController } from '../../../model/hooks'
import { AutocompleteRow, BoldText } from './styles'
import { useCounterpartiesOptions } from '../../../model/hooks/useGeneralInfoCounterpartiesHelpers'
import { Link } from 'react-router-dom'

interface GenericInfoFormProps {
    isPreview: boolean
    isCompany?: boolean
}

export const GenericInfoForm: FC<GenericInfoFormProps> = ({
    isCompany,
    isPreview,
}) => {
    const {
        isDirty,
        isLoading,
        orderId,
        isCreate,
        isButtonDisabled,
        isOneOfTheBookingsAlreadyCreated,
        onGoNext,
        onCounterpartyChange,
        createCounterpartyAndChange,
    } = useGeneralInfoController(isCompany)

    const {
        optionInputValue,
        currentCounterpartyOption,
        counterpartiesOptions,
        fetchNextPage,
        isFetching,
        onChangeNumber,
        onChangeName,
        onChangeInn,
        onChangeEmail,
        onChangeContactName,

        currentContactOptionValue,
        updateContactValue,
        contactsOptions,
        onChangeContactsPhone,
        onChangeContactsEmail,
        onChangeContactsFio,

        counterpartyRedirectUrl,
    } = useCounterpartiesOptions(isCompany)

    return (
        <div className='content'>
            {!isCreate && (
                <div className='row' style={{ flexWrap: 'nowrap' }}>
                    <Input
                        name=''
                        label='Номер заказа'
                        value={orderId?.toString()}
                        disabled
                    />
                    <Input
                        name=''
                        value={isPreview ? 'Просмотр' : 'Редактирование'}
                        disabled
                    />
                </div>
            )}
            <AutocompleteRow>
                <Autocomplete
                    inputValue={optionInputValue.phone}
                    disabled={isLoading || isPreview}
                    value={currentCounterpartyOption.phone ?? null}
                    onChange={onCounterpartyChange}
                    options={counterpartiesOptions.phone}
                    label='Контактный телефон'
                    placeholder='Введите номер телефона...'
                    onLoadMore={fetchNextPage}
                    onInputChange={onChangeNumber}
                    isLoading={isFetching}
                    onCreateOption={phone =>
                        createCounterpartyAndChange({ phone })
                    }
                    required
                />
            </AutocompleteRow>

            {isCompany ? (
                <>
                    <Autocomplete
                        inputValue={optionInputValue.name}
                        disabled={isLoading || isPreview}
                        value={currentCounterpartyOption.name ?? null}
                        onChange={onCounterpartyChange}
                        options={counterpartiesOptions.name}
                        label='Наименование юр. лица'
                        placeholder='Введите наименование...'
                        onLoadMore={fetchNextPage}
                        onInputChange={onChangeName}
                        isLoading={isFetching}
                        onCreateOption={organizationName =>
                            createCounterpartyAndChange({ organizationName })
                        }
                        required
                    />
                    <Autocomplete
                        inputValue={optionInputValue.inn}
                        disabled={isLoading || isPreview}
                        value={currentCounterpartyOption.inn ?? null}
                        onChange={onCounterpartyChange}
                        options={counterpartiesOptions.inn}
                        label='ИНН'
                        placeholder='Введите ИНН...'
                        onLoadMore={fetchNextPage}
                        onInputChange={onChangeInn}
                        isLoading={isFetching}
                        onCreateOption={inn =>
                            createCounterpartyAndChange({ inn })
                        }
                        required
                    />
                    <BoldText>Контактные данные связующего лица</BoldText>
                    <Autocomplete
                        inputValue={optionInputValue.contactsPhone}
                        disabled={isLoading || isPreview}
                        value={currentContactOptionValue.phone ?? null}
                        onChange={option => {
                            updateContactValue(option)
                            onCounterpartyChange(option)
                        }}
                        options={contactsOptions.phone}
                        onInputChange={onChangeContactsPhone}
                        label='Контактный телефон'
                        placeholder='Введите телефон'
                        isLoading={isFetching}
                        onCreateOption={phone =>
                            createCounterpartyAndChange({
                                phone:
                                    currentContactOptionValue.phone?.payload
                                        ?.phone ?? undefined,
                                organizationName:
                                    currentCounterpartyOption.name?.payload
                                        ?.additionalData?.documents
                                        ?.organizationName ?? undefined,
                                inn:
                                    currentCounterpartyOption.inn?.payload
                                        ?.additionalData?.documents?.inn ??
                                    undefined,
                                email: currentCounterpartyOption.name?.payload
                                    ?.email,
                                contacts: [
                                    {
                                        email: '',
                                        phone,
                                        firstName: '',
                                        surname: '',
                                        patronymic: '',
                                    },
                                ],
                            })
                        }
                        required
                    />
                    <Autocomplete
                        inputValue={optionInputValue.contactsEmail}
                        disabled={isLoading || isPreview}
                        value={currentContactOptionValue.email ?? null}
                        onChange={option => {
                            updateContactValue(option)
                            onCounterpartyChange(option)
                        }}
                        options={contactsOptions.email}
                        onInputChange={onChangeContactsEmail}
                        label='Контактный Email'
                        placeholder='Введите Email'
                        isLoading={isFetching}
                        onCreateOption={email =>
                            createCounterpartyAndChange({
                                phone:
                                    currentContactOptionValue.phone?.payload
                                        ?.phone ?? undefined,
                                organizationName:
                                    currentCounterpartyOption.name?.payload
                                        ?.additionalData?.documents
                                        ?.organizationName ?? undefined,
                                inn:
                                    currentCounterpartyOption.inn?.payload
                                        ?.additionalData?.documents?.inn ??
                                    undefined,
                                email: currentCounterpartyOption.name?.payload
                                    ?.email,
                                contacts: [
                                    {
                                        email,
                                        phone: '',
                                        firstName: '',
                                        surname: '',
                                        patronymic: '',
                                    },
                                ],
                            })
                        }
                        required
                    />
                    <Autocomplete
                        inputValue={optionInputValue.contactsFio}
                        disabled={isLoading || isPreview}
                        value={currentContactOptionValue.contacts ?? null}
                        onChange={option => {
                            updateContactValue(option)
                            onCounterpartyChange(option)
                        }}
                        onInputChange={onChangeContactsFio}
                        options={contactsOptions.contacts}
                        label='Контактный ФИО'
                        placeholder='Введите телефон'
                        isLoading={isFetching}
                        onCreateOption={fio => {
                            const fullName = fio.split(' ')
                            return createCounterpartyAndChange({
                                phone:
                                    currentContactOptionValue.phone?.payload
                                        ?.phone ?? undefined,
                                organizationName:
                                    currentCounterpartyOption.name?.payload
                                        ?.additionalData?.documents
                                        ?.organizationName ?? undefined,
                                inn:
                                    currentCounterpartyOption.inn?.payload
                                        ?.additionalData?.documents?.inn ??
                                    undefined,
                                email: currentCounterpartyOption.name?.payload
                                    ?.email,
                                contacts: [
                                    {
                                        email: '',
                                        phone: '',
                                        firstName: fullName?.[1] ?? '',
                                        surname: fullName?.[0] ?? '',
                                        patronymic: fullName?.[2] ?? '',
                                    },
                                ],
                            })
                        }}
                        required
                    />
                </>
            ) : (
                <>
                    <Autocomplete
                        inputValue={optionInputValue.name}
                        disabled={isLoading || isPreview}
                        value={currentCounterpartyOption.contacts ?? null}
                        onChange={onCounterpartyChange}
                        options={counterpartiesOptions.contacts}
                        label='ФИО'
                        placeholder='Введите ФИО'
                        onLoadMore={fetchNextPage}
                        onInputChange={onChangeContactName}
                        isLoading={isFetching}
                        onCreateOption={name =>
                            createCounterpartyAndChange({ name })
                        }
                        required
                    />
                    <Autocomplete
                        inputValue={optionInputValue.email}
                        disabled={isLoading || isPreview}
                        value={currentCounterpartyOption.email ?? null}
                        onChange={onCounterpartyChange}
                        options={counterpartiesOptions.email}
                        label='Контактный Email'
                        placeholder='Введите Email'
                        onLoadMore={fetchNextPage}
                        onInputChange={onChangeEmail}
                        isLoading={isFetching}
                        onCreateOption={email =>
                            createCounterpartyAndChange({ email })
                        }
                        required
                    />
                </>
            )}

            {counterpartyRedirectUrl && (
                <Link to={counterpartyRedirectUrl} target='_blank'>
                    Перейти в профиль клиента
                </Link>
            )}

            {!isPreview && (
                <Button disabled={isButtonDisabled} onClick={onGoNext}>
                    {isOneOfTheBookingsAlreadyCreated
                        ? isDirty
                            ? 'Сохранить'
                            : 'Далее'
                        : 'Далее'}
                </Button>
            )}
        </div>
    )
}
